import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const PLANO_LABEL = { basic: 'Basic', pro: 'Pro', full: 'Full', enterprise: 'Enterprise' };
const CALIB_LABEL = { full: 'Interna + Externa', external_only: 'Somente Externa' };

const TODOS_MODULOS = ['dashboard','ativos','instrumentos','malhas','calibracoes','padroes','verificacoes','orcamentos','calendario','relatorios'];
const MODULO_LABEL = {
  dashboard: 'Dashboard', ativos: 'Ativos', instrumentos: 'Instrumentos',
  malhas: 'Malhas', calibracoes: 'Calibração', padroes: 'Padrões de Referência',
  verificacoes: 'Verificações', orcamentos: 'Orçamentos',
  calendario: 'Calendário', relatorios: 'Relatórios',
};
const MODULOS_CORE = ['dashboard', 'instrumentos', 'calibracoes'];

const BADGE_ATIVO = 'bg-emerald-900/40 text-emerald-300 border border-emerald-800/50';
const BADGE_INATIVO = 'bg-slate-700 text-slate-400 border border-slate-600';

function Badge({ ativo }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ativo ? BADGE_ATIVO : BADGE_INATIVO}`}>
      {ativo ? 'Ativa' : 'Inativa'}
    </span>
  );
}

// ─── Modal: Nova Empresa ───────────────────────────────────────────────────────
function ModalNovaEmpresa({ onFechar, onCriado }) {
  const [form, setForm] = useState({
    tenantName: '', adminName: '', adminEmail: '', adminPassword: '',
    plan: 'basic', planoCalibracao: 'full',
  });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async () => {
    setErro('');
    if (!form.tenantName || !form.adminName || !form.adminEmail || !form.adminPassword) {
      return setErro('Preencha todos os campos obrigatórios.');
    }
    setLoading(true);
    try {
      await api.post('/admin/tenants', form);
      onCriado();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao criar empresa.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--surface-4)]">
          <h2 className="font-semibold text-slate-100">Nova Empresa</h2>
          <button onClick={onFechar} className="text-slate-500 hover:text-slate-300 text-xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Nome da empresa *</label>
            <input
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              value={form.tenantName}
              onChange={e => setForm(p => ({ ...p, tenantName: e.target.value }))}
              placeholder="Ex: Empresa XYZ Ltda"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Plano</label>
              <select
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.plan}
                onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
              >
                {Object.entries(PLANO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Calibração</label>
              <select
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.planoCalibracao}
                onChange={e => setForm(p => ({ ...p, planoCalibracao: e.target.value }))}
              >
                {Object.entries(CALIB_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <hr className="border-[var(--surface-4)]" />
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Supervisor inicial</p>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Nome *</label>
            <input
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              value={form.adminName}
              onChange={e => setForm(p => ({ ...p, adminName: e.target.value }))}
              placeholder="Nome completo"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">E-mail *</label>
              <input
                type="email"
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.adminEmail}
                onChange={e => setForm(p => ({ ...p, adminEmail: e.target.value }))}
                placeholder="supervisor@empresa.com"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Senha *</label>
              <input
                type="password"
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.adminPassword}
                onChange={e => setForm(p => ({ ...p, adminPassword: e.target.value }))}
                placeholder="Mín. 6 caracteres"
              />
            </div>
          </div>

          {erro && <p className="text-red-400 text-xs">{erro}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--surface-4)]">
          <button onClick={onFechar} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 transition-colors">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={loading}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors"
          >
            {loading ? 'Criando...' : 'Criar Empresa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Editar Empresa ─────────────────────────────────────────────────────
function ModalEditarEmpresa({ empresa, onFechar, onSalvo }) {
  // Initialize selected modules: empty = all active → pre-check all; otherwise use the stored set
  const modulosIniciais = empresa.modulosAtivos?.length > 0 ? empresa.modulosAtivos : TODOS_MODULOS;

  const [form, setForm] = useState({
    name: empresa.name,
    nomeEmpresa: empresa.nomeEmpresa || empresa.name,
    plan: empresa.plan || 'basic',
    planoCalibracao: empresa.planoCalibracao || 'full',
    active: empresa.active,
  });
  const [modulosSelecionados, setModulosSelecionados] = useState(modulosIniciais);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const toggleModulo = (key) => {
    if (MODULOS_CORE.includes(key)) return;
    setModulosSelecionados(prev =>
      prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
    );
  };

  const salvar = async () => {
    setErro('');
    setLoading(true);
    // If all modules are selected, save [] (= all active, backward compat)
    const modulosAtivos = modulosSelecionados.length === TODOS_MODULOS.length ? [] : modulosSelecionados;
    try {
      await api.put(`/admin/tenants/${empresa.id}`, { ...form, modulosAtivos });
      onSalvo();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--surface-4)] flex-shrink-0">
          <h2 className="font-semibold text-slate-100">Editar Empresa</h2>
          <button onClick={onFechar} className="text-slate-500 hover:text-slate-300 text-xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Slug (identificador interno)</label>
            <input
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Nome exibido no sistema</label>
            <input
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              value={form.nomeEmpresa}
              onChange={e => setForm(p => ({ ...p, nomeEmpresa: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Plano</label>
              <select
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.plan}
                onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
              >
                {Object.entries(PLANO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Calibração</label>
              <select
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                value={form.planoCalibracao}
                onChange={e => setForm(p => ({ ...p, planoCalibracao: e.target.value }))}
              >
                {Object.entries(CALIB_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Módulos ativos */}
          <div>
            <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wider">Módulos ativos</label>
            <div className="grid grid-cols-2 gap-1.5">
              {TODOS_MODULOS.map(key => {
                const isCore = MODULOS_CORE.includes(key);
                const ativo = modulosSelecionados.includes(key);
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      ativo
                        ? 'bg-blue-950/40 border-blue-800/50 text-slate-200'
                        : 'bg-[var(--surface-3)] border-[var(--surface-4)] text-slate-500'
                    } ${isCore ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="accent-blue-500 w-3.5 h-3.5"
                      checked={ativo}
                      disabled={isCore}
                      onChange={() => toggleModulo(key)}
                    />
                    <span className="text-xs">{MODULO_LABEL[key]}</span>
                    {isCore && <span className="text-[9px] text-slate-600 ml-auto">core</span>}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, active: !p.active }))}
              className={`relative w-10 h-5 rounded-full transition-colors ${form.active ? 'bg-blue-600' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.active ? 'left-5' : 'left-0.5'}`} />
            </button>
            <span className="text-sm text-slate-300">{form.active ? 'Empresa ativa' : 'Empresa inativa'}</span>
          </div>

          {erro && <p className="text-red-400 text-xs">{erro}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--surface-4)] flex-shrink-0">
          <button onClick={onFechar} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 transition-colors">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={loading}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Painel: Usuários da empresa ───────────────────────────────────────────────
function PainelUsuarios({ tenantId, onFechar }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/tenants/${tenantId}/users`)
      .then(r => setUsers(r.data))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const ROLE_LABEL = { supervisor: 'Supervisor', operador: 'Operador', revisor: 'Revisor', consulta: 'Consulta' };
  const ROLE_COLOR = {
    supervisor: 'bg-purple-900/40 text-purple-300',
    operador: 'bg-blue-900/40 text-blue-300',
    revisor: 'bg-amber-900/40 text-amber-300',
    consulta: 'bg-slate-700 text-slate-300',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--surface-4)]">
          <h2 className="font-semibold text-slate-100">Usuários</h2>
          <button onClick={onFechar} className="text-slate-500 hover:text-slate-300 text-xl leading-none">×</button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="text-slate-400 text-sm text-center py-6">Carregando...</p>
          ) : users.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-6">Nenhum usuário.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[var(--surface-4)]">
                  <th className="text-left pb-2">Nome</th>
                  <th className="text-left pb-2">E-mail</th>
                  <th className="text-left pb-2">Role</th>
                  <th className="text-left pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--surface-4)]">
                {users.map(u => (
                  <tr key={u.id} className="text-slate-300">
                    <td className="py-2 pr-3">{u.name}</td>
                    <td className="py-2 pr-3 text-slate-400 text-xs">{u.email}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${ROLE_COLOR[u.role] || 'bg-slate-700 text-slate-300'}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="py-2">
                      <Badge ativo={u.active !== false} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────────
export default function AdminEmpresasPage() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modalNova, setModalNova] = useState(false);
  const [editando, setEditando] = useState(null);
  const [verUsuarios, setVerUsuarios] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get('/admin/tenants');
      setEmpresas(data);
    } catch {
      setErro('Erro ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const totais = {
    total: empresas.length,
    ativas: empresas.filter(e => e.active).length,
    instrumentos: empresas.reduce((s, e) => s + (e._count?.instruments || 0), 0),
  };

  return (
    <div className="min-h-screen bg-[var(--surface-1)] text-slate-200 p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-100">Gestão de Empresas</h1>
          <p className="text-sm text-slate-500 mt-0.5">Painel administrativo — apenas você vê isso</p>
        </div>
        <button
          onClick={() => setModalNova(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
        >
          + Nova Empresa
        </button>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total de empresas', valor: totais.total, cor: 'text-blue-400' },
          { label: 'Empresas ativas', valor: totais.ativas, cor: 'text-emerald-400' },
          { label: 'Instrumentos (total)', valor: totais.instrumentos, cor: 'text-violet-400' },
        ].map(c => (
          <div key={c.label} className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-4">
            <p className="text-xs text-slate-500">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${c.cor}`}>{c.valor}</p>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-slate-400 text-sm text-center py-12">Carregando...</p>
        ) : erro ? (
          <p className="text-red-400 text-sm text-center py-12">{erro}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-3)]">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[var(--surface-4)]">
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Plano</th>
                <th className="text-left px-4 py-3">Calibração</th>
                <th className="text-center px-4 py-3">Usuários</th>
                <th className="text-center px-4 py-3">Instrumentos</th>
                <th className="text-center px-4 py-3">Calibrações</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Criado em</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--surface-4)]">
              {empresas.map(emp => (
                <tr key={emp.id} className="hover:bg-[var(--surface-3)]/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{emp.nomeEmpresa || emp.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{emp.name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {PLANO_LABEL[emp.plan] || emp.plan}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      emp.planoCalibracao === 'full'
                        ? 'bg-blue-900/40 text-blue-300 border border-blue-800/50'
                        : 'bg-slate-700 text-slate-400 border border-slate-600'
                    }`}>
                      {CALIB_LABEL[emp.planoCalibracao] || emp.planoCalibracao}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setVerUsuarios(emp.id)}
                      className="text-blue-400 hover:text-blue-300 font-medium hover:underline"
                    >
                      {emp._count?.users ?? 0}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-400">{emp._count?.instruments ?? 0}</td>
                  <td className="px-4 py-3 text-center text-slate-400">{emp._count?.calibrations ?? 0}</td>
                  <td className="px-4 py-3"><Badge ativo={emp.active} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {new Date(emp.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setEditando(emp)}
                      className="px-3 py-1 rounded-lg text-xs bg-[var(--surface-3)] hover:bg-[var(--surface-4)] text-slate-300 transition-colors"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalNova && (
        <ModalNovaEmpresa
          onFechar={() => setModalNova(false)}
          onCriado={() => { setModalNova(false); carregar(); }}
        />
      )}
      {editando && (
        <ModalEditarEmpresa
          empresa={editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => { setEditando(null); carregar(); }}
        />
      )}
      {verUsuarios && (
        <PainelUsuarios
          tenantId={verUsuarios}
          onFechar={() => setVerUsuarios(null)}
        />
      )}
    </div>
  );
}
